import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { CircularProgress } from '@mui/material'
import Api from '../api'
import PortfolioHistoryPanel from '../panels/PortfolioHistoryPanel'
import PortfolioHistoryParameter from '../charts/PortfolioHistory/PortfolioHistoryParameter'
import PortfolioHistoryChart from '../charts/PortfolioHistory/PortfolioHistoryChart'
import NoSite from '../components/NoSite'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import moment from 'moment-timezone'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'

const useStyles = makeStyles(theme => ({
  paramContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  wraperContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '30px',
    width: '100%',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
}))

const convertUtcStringToTimezone = (utcString, timezone) => {
  return moment
    .tz(utcString, 'DD/MM/YYYY HH:mm', 'UTC') // <-- updated input format
    .tz(timezone)
    .format('YYYY-MM-DD HH:mm') // <-- output format stays the same
}

const PortfolioHistoryPage = props => {
  const classes = useStyles(props)
  const { container, selectedSetup, setSelectedUserSetup, userSetups } = props
  const [portfolioHistoryData, setPortfolioHistoryData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [nodataContent, setNodataContent] = useState({})
  // for disabling inputs in form
  const [trackDisableStatus, setTrackDisableStatus] = useState(false)

  const currentDate = new Date()
  const twoYearsAgo = new Date()
  twoYearsAgo.setFullYear(currentDate.getFullYear() - 2)

  const initialDateRange = { startDate: twoYearsAgo, endDate: currentDate }
  const [selectedDateRange, setSelectedDateRange] = useState(initialDateRange)

  // use this timezone from store because of refreshing page
  const timezoneStore =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  // If there is only 1 available setup, select it automatically
  useEffect(() => {
    if (userSetups.length === 1 && selectedSetup !== userSetups[0]) {
      setSelectedUserSetup(userSetups[0])
    }
  }, [userSetups, selectedSetup, setSelectedUserSetup])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setTrackDisableStatus(true)
      try {
        const response = await Api.History.getHistory(
          selectedSetup,
          selectedDateRange,
        )
        if (response.status === 204) {
          setPortfolioHistoryData(null)
          setTrackDisableStatus(false)
          setNodataContent({
            heading: `No data for selected setup: ${selectedSetup}`,
            body: '',
          })
        }
        if (response.status === 200) {
          setPortfolioHistoryData(response.data)
        }

        setLoading(false)
      } catch (error) {
        setPortfolioHistoryData(null)
        setTrackDisableStatus(false)
        if (error.response.status === 411) {
          setNodataContent({
            heading: `Error running query for selected setup: ${selectedSetup}`,
            body: '',
          })
        } else {
          setNodataContent({
            heading: `Error fetching data for selected setup: ${selectedSetup}`,
            body: '',
          })
        }
        console.error('Error fetching portfolio history data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (selectedSetup !== null) {
      fetchData()
    } else {
      setPortfolioHistoryData(null)
      setNodataContent({
        heading: 'No data',
        body: 'Select a setup to view portfolio history',
      })
    }
  }, [selectedSetup, selectedDateRange])

  const resultPanel = () => {
    return portfolioHistoryData !== null ? (
      <div className={classes.wraperContainer}>
        <div className={classes.paramContainer}>
          <PortfolioHistoryParameter
            label={'Last change to Portfolio'}
            value={convertUtcStringToTimezone(
              portfolioHistoryData['lastChanges'],
              timezoneStore,
            )}
            hasTooltip={true}
          />
          <PortfolioHistoryParameter
            label={'Installed capacity (MW)'}
            value={portfolioHistoryData['installedCapacity']}
            arrowValue={portfolioHistoryData['diffInstalledCapacity']}
          />
          <PortfolioHistoryParameter
            label={'Plants'}
            value={portfolioHistoryData['numberOfPlants']}
            arrowValue={portfolioHistoryData['numberOfPlantsDiff']}
          />
          <PortfolioHistoryParameter
            label={'BasedOn Plants'}
            value={portfolioHistoryData['numberOfBasedOn']}
            arrowValue={portfolioHistoryData['numberOfBasedOnDiff']}
          />
        </div>
        <PortfolioHistoryChart
          chartData={portfolioHistoryData['history']}
          chartName={'chartPortfolioHistory'}
          selectedSetup={selectedSetup}
          setTrackDisableStatus={setTrackDisableStatus}
        ></PortfolioHistoryChart>
      </div>
    ) : (
      <div className={classes.centerItem}>
        <NoSite
          heading={nodataContent.heading}
          body={nodataContent.body}
        ></NoSite>
      </div>
    )
  }

  return (
    <>
      <PortfolioHistoryPanel
        trackDisableStatus={trackDisableStatus}
        container={container}
        userSetups={userSetups}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
      >
        {loading ? (
          <div className={classes.centerItem}>
            <CircularProgress />
          </div>
        ) : (
          resultPanel()
        )}
      </PortfolioHistoryPanel>
    </>
  )
}

const mapStateToProps = state => {
  return {
    selectedSetup: state.userSetups.selectedSetup,
    userSetups: state.userSetups.userSetups,
  }
}

const mapDispatchToProps = {
  setSelectedUserSetup,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PortfolioHistoryPage)
